import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Button, Table, Typography, Sheet, IconButton, Modal, ModalDialog,
  ModalClose, Alert
} from '@mui/joy';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../services/api';
import CategoryForm from './CategoryForm';
import SearchInput from './SearchInput';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';

function CategoryManager({ onRefresh }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState(null);

  const showAlert = (message, color = 'success') => {
    setAlert({ message, color });
    setTimeout(() => setAlert(null), 4000);
  };

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      showAlert('Не удалось загрузить категории', 'danger');
      setCategories([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter(c => c.name.toLowerCase().includes(term));
  }, [categories, search]);

  const handleOpenAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleOpenEdit = (category) => {
    setEditing(category);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSave = async (data) => {
    try {
      if (editing) {
        await updateCategory(editing.id, data);
        showAlert('Категория обновлена');
      } else {
        await createCategory(data);
        showAlert('Категория добавлена');
      }
      handleCloseForm();
      fetchCategories();
      if (onRefresh) onRefresh();
    } catch (e) {
      showAlert('Ошибка сохранения: ' + (e.response?.data?.error || e.message), 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить категорию?')) return;
    try {
      await deleteCategory(id);
      showAlert('Категория удалена');
      fetchCategories();
      if (onRefresh) onRefresh();
    } catch (e) {
      showAlert('Ошибка удаления: ' + (e.response?.data?.error || e.message), 'danger');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography level="h3">Категории</Typography>
        <Button startDecorator={<AddIcon />} onClick={handleOpenAdd}>Добавить категорию</Button>
      </Box>

      {alert && (
        <Alert color={alert.color} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 16, mb: 2 }}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Поиск по названию..."
        />
      </Sheet>

      {loading ? (
        <Typography>Загрузка...</Typography>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 16, overflow: 'hidden' }}>
          <Table size="sm" stickyHeader>
            <thead>
              <tr>
                <th style={{ width: 80 }}>ID</th>
                <th>Название</th>
                <th style={{ width: 110 }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan={3}>
                    <Box sx={{ py: 6, textAlign: 'center', color: 'neutral.500' }}>
                      <CategoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                      <Typography>Категории не найдены</Typography>
                    </Box>
                  </td>
                </tr>
              ) : (
                filteredCategories.map((c) => (
                  <tr key={c.id}>
                    <td>{c.id}</td>
                    <td>
                      <Typography level="body-sm" fontWeight="md">{c.name}</Typography>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="sm" variant="soft" color="primary" onClick={() => handleOpenEdit(c)}><EditIcon /></IconButton>
                        <IconButton size="sm" variant="soft" color="danger" onClick={() => handleDelete(c.id)}><DeleteIcon /></IconButton>
                      </Box>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Sheet>
      )}

      <Modal open={showForm} onClose={handleCloseForm}>
        <ModalDialog
          variant="outlined"
          sx={{
            width: { xs: '95%', sm: 460 },
            borderRadius: 20,
            p: 0,
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="h4">
              {editing ? 'Редактировать категорию' : 'Добавить категорию'}
            </Typography>
            <ModalClose onClick={handleCloseForm} />
          </Box>
          <Box sx={{ p: 3 }}>
            <CategoryForm
              initialData={editing}
              onSave={handleSave}
              onCancel={handleCloseForm}
            />
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}

export default CategoryManager;
